const croppedImageCacheStore = new Map() // The data urls took up too much space to store in zion session cache

const buildCacheKey = (pageNumber, positionIndex) => `croppedImage:${pageNumber}:${positionIndex}`

const isDevelopment = process.env.NODE_ENV === 'development'

const logDebug = (...args) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.debug('[croppedImageCache]', ...args)
  }
}
const normalizeCropData = (cropData = {}) => ({
  x: Number(cropData.x ?? 0),
  y: Number(cropData.y ?? 0),
  w: Number(cropData.w ?? 0),
  h: Number(cropData.h ?? 0),
  rotation: Number(cropData.rotation ?? 0),
})

const isValidEntry = (entry, { memoryId, imageUrl, cropData }) => {
  if (!entry || typeof entry !== 'object') {
    return false
  }

  const { memoryId: cachedMemoryId, imageUrl: cachedUrl, cropData: cachedCropData } = entry

  if (!cachedMemoryId || !cachedUrl || !cachedCropData) {
    return false
  }

  const normalizedCachedCrop = normalizeCropData(cachedCropData)

  return (
    cachedMemoryId === memoryId &&
    cachedUrl === imageUrl &&
    normalizedCachedCrop.x === cropData.x &&
    normalizedCachedCrop.y === cropData.y &&
    normalizedCachedCrop.w === cropData.w &&
    normalizedCachedCrop.h === cropData.h &&
    normalizedCachedCrop.rotation === cropData.rotation
  )
}

/**
 * Retrieve a cached cropped image data URL if it matches the provided validation parameters.
 * @param {Object} params
 * @param {number|string} params.pageNumber - Page number associated with the image slot.
 * @param {number} params.positionIndex - Element position index within the page layout.
 * @param {string} params.memoryId - Memory identifier for the underlying image.
 * @param {string} params.imageUrl - Source URL for the image.
 * @param {Object|string} params.cropData - Crop rectangle data.
 * @returns {Promise<string|null>} The cached data URL or null if no valid entry exists.
 */
export const getCroppedImageCache = async ({ pageNumber, positionIndex, memoryId, imageUrl, cropData }) => {
  if (!memoryId || !imageUrl || !cropData) {
    return null
  }

  const normalizedCrop = normalizeCropData(typeof cropData === 'string' ? JSON.parse(cropData) : cropData)

  try {
    const cacheKey = buildCacheKey(pageNumber, positionIndex)
    const cachedEntry = croppedImageCacheStore.get(cacheKey)

    if (!isValidEntry(cachedEntry, { memoryId, imageUrl, cropData: normalizedCrop })) {
      if (cachedEntry && typeof cachedEntry === 'object') {
        logDebug('Cache miss due to validation failure', { cacheKey, cachedEntry, memoryId, imageUrl, normalizedCrop })
      }
      return null
    }

    return cachedEntry.croppedDataUrl || null
  } catch (error) {
    logDebug('Cache lookup failed', error)
    return null
  }
}

/**
 * Persist a cropped image data URL for the given page/position combination.
 * @param {Object} params
 * @param {number|string} params.pageNumber
 * @param {number} params.positionIndex
 * @param {string} params.memoryId
 * @param {string} params.imageUrl
 * @param {Object|string} params.cropData
 * @param {string} params.croppedDataUrl
 * @returns {Promise<void>}
 */
export const setCroppedImageCache = async ({
  pageNumber,
  positionIndex,
  memoryId,
  imageUrl,
  cropData,
  croppedDataUrl,
}) => {
  if (!memoryId || !imageUrl || !cropData || !croppedDataUrl) {
    return
  }

  const normalizedCrop = normalizeCropData(typeof cropData === 'string' ? JSON.parse(cropData) : cropData)

  const cacheKey = buildCacheKey(pageNumber, positionIndex)
  const payload = {
    memoryId,
    imageUrl,
    cropData: normalizedCrop,
    croppedDataUrl,
  }

  try {
    croppedImageCacheStore.set(cacheKey, payload)
    logDebug('Cache write success', { cacheKey })
  } catch (error) {
    logDebug('Cache write failed', { cacheKey, error })
  }
}

/**
 * Remove a cached crop for the given page/position combination.
 * @param {Object} params
 * @param {number|string} params.pageNumber
 * @param {number} params.positionIndex
 * @returns {Promise<void>}
 */
export const clearCroppedImageCache = async ({ pageNumber, positionIndex }) => {
  try {
    const cacheKey = buildCacheKey(pageNumber, positionIndex)
    croppedImageCacheStore.delete(cacheKey)
  } catch (error) {
    logDebug('Cache remove failed', error)
  }
}
